/**
 * MỘT nơi duy nhất biết đường dẫn của API.
 *
 * Rải `"/internal/auth/me"` khắp component thì đổi một đường dẫn là một cuộc truy lùng chuỗi, và bỏ
 * sót một chỗ nghĩa là đúng màn hình đó im lặng 404 trong khi mọi màn khác vẫn chạy.
 */
const integrationRoot='/internal/integrations';
const partnerConnectionPath=(id:string)=>`${integrationRoot}/connections/${encodeURIComponent(id)}`;

export const paths = {
  integrationDocuments: (code: string, scope: string) => `/internal/integration-documents/${encodeURIComponent(code)}/${encodeURIComponent(scope)}`,
  integrationDocumentFile: (id: string) => `/internal/integration-documents/files/${encodeURIComponent(id)}/content`,
  featureReadiness:()=>"/internal/feature-readiness",
  capabilityRollouts: {
    list:()=>"/internal/capability-rollouts",
    item:(supplier:string,capability:string)=>`/internal/capability-rollouts/${encodeURIComponent(supplier)}/${encodeURIComponent(capability)}`,
  },
  partnerReleases: {
    policyRollouts:()=>"/internal/partner-releases/policy-rollouts",
    enforcePolicy:(app:string)=>`/internal/partner-releases/policy-rollouts/${encodeURIComponent(app)}/enforce`,
    list:()=>"/internal/partner-releases",
    certifications:()=>'/internal/partner-releases/certifications',
    candidates:()=>'/internal/partner-releases/certification-candidates',
    issue:()=>'/internal/partner-releases/certifications/issue',
    manualApproval:()=>'/internal/partner-releases/manual-approvals',
    item:(ownerApp:string,partnerCode:string,environment:string)=>`/internal/partner-releases/${encodeURIComponent(ownerApp)}/${encodeURIComponent(partnerCode)}/${encodeURIComponent(environment)}`,
    revoke:(certificationId:string)=>`/internal/partner-releases/certifications/${encodeURIComponent(certificationId)}/revoke`,
  },
  partnerConnections: {
    accounts:()=>`${integrationRoot}/accounts`,
    connections:()=>`${integrationRoot}/connections`,
    connection:partnerConnectionPath,
    versions:(id:string)=>`${partnerConnectionPath(id)}/versions`,
    test:(id:string,versionId:string)=>`${partnerConnectionPath(id)}/versions/${encodeURIComponent(versionId)}/test`,
    restore:(id:string,revision:number)=>`${partnerConnectionPath(id)}/versions/${revision}/restore`,
    activate:(id:string)=>`${partnerConnectionPath(id)}/activate`,
    cutGrace:(id:string)=>`${partnerConnectionPath(id)}/grace/cut`,
    bindings:()=>`${integrationRoot}/bindings`,
    compare:(id:string,from:number,to:number)=>`${partnerConnectionPath(id)}/compare?from=${from}&to=${to}`,
    pricing:(id:string)=>`${partnerConnectionPath(id)}/pricing`,
    pricingEntry:(id:string,entryId:string)=>`${partnerConnectionPath(id)}/pricing/${encodeURIComponent(entryId)}`,
    disable:(id:string)=>`${partnerConnectionPath(id)}/disable`,
  },
  auth: {
    login: () => "/internal/auth/login",
    logout: () => "/internal/auth/logout",
    refresh: () => "/internal/auth/refresh",
    me: () => "/internal/auth/me",
    changePassword: () => "/internal/auth/change-password",
    /** Chỉ máy dev (`DEPLOYMENT_MODE=local`) — production trả 404. Xem `dev-login.controller.ts`. */
    devLogin: () => "/dev/login",
  },
  fleet: {
    connectors: () => "/internal/fleet/connectors",
    connector: (slug: string) => `/internal/fleet/connectors/${encodeURIComponent(slug)}`,
    resources: () => "/internal/fleet/resources",
    services: () => "/internal/fleet/services",
    /** HQ-2026-018 — chi phí hạ tầng theo app (bản chụp ngày). */
    costOverview: () => "/internal/cost/overview",
    /** PLAT-2026-117 — hub hỏi sống máy chủ từng app (server → server). */
    mailStatus: () => "/internal/mail-status",
    /** PLAT-2026-120 — « Gửi thử »: app gửi một thư thật qua đường gửi của nó. */
    mailTest: (app: string) => `/internal/mail-status/${encodeURIComponent(app)}/test`,
    /** PLAT-2026-135 lát H — mail của một app từ bản hub đã chép (trạng thái sống + thư hỏng + vòng đời + lịch sử). */
    mailApp: (app: string) => `/internal/mail-status/${encodeURIComponent(app)}`,
    mailHistory: (app: string) => `/internal/mail-status/${encodeURIComponent(app)}/history`,
    mailList: (app: string) => `/internal/mail-status/${encodeURIComponent(app)}/mails`,
    mailLifecycle: (app: string, mailId: string) =>
      `/internal/mail-status/${encodeURIComponent(app)}/mails/${encodeURIComponent(mailId)}`,
    /** PLAT-2026-135 lát N — tài khoản nhà cung cấp mail (đọc bằng khoá quản trị ở hub; không route nào trả khoá). */
    mailAccounts: () => "/internal/mail-accounts",
    mailAccount: (setId: string) => `/internal/mail-accounts/${encodeURIComponent(setId)}`,
    mailAccountRefresh: (setId: string) => `/internal/mail-accounts/${encodeURIComponent(setId)}/refresh`,
    mailAccountWebhook: (setId: string) => `/internal/mail-accounts/${encodeURIComponent(setId)}/webhook`,
    mailAccountDomains: (setId: string) => `/internal/mail-accounts/${encodeURIComponent(setId)}/domains`,
    mailAccountDomainVerify: (setId: string, domainId: string) =>
      `/internal/mail-accounts/${encodeURIComponent(setId)}/domains/${encodeURIComponent(domainId)}/verify`,
    mailAccountDomainRemove: (setId: string, domainId: string) =>
      `/internal/mail-accounts/${encodeURIComponent(setId)}/domains/${encodeURIComponent(domainId)}/remove`,
    mailAccountApiKeys: (setId: string) => `/internal/mail-accounts/${encodeURIComponent(setId)}/api-keys`,
    mailAccountApiKeyRemove: (setId: string, keyId: string) =>
      `/internal/mail-accounts/${encodeURIComponent(setId)}/api-keys/${encodeURIComponent(keyId)}/remove`,
    mailAccountSuppressions: (setId: string, cursor?: string) =>
      `/internal/mail-accounts/${encodeURIComponent(setId)}/suppressions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    mailAccountSuppressionRemove: (setId: string) => `/internal/mail-accounts/${encodeURIComponent(setId)}/suppressions/remove`,
    integrationRoutesOverview: (category: string) => `/internal/integration-routes/${encodeURIComponent(category)}`,
    integrationRouteSet: (category: string, app: string) =>
      `/internal/integration-routes/${encodeURIComponent(category)}/app/${encodeURIComponent(app)}`,
    integrationRouteGates: (category: string, app: string) =>
      `/internal/integration-routes/${encodeURIComponent(category)}/app/${encodeURIComponent(app)}/gates`,
    integrationRoutePolicy: (category: string, app: string) =>
      `/internal/integration-routes/${encodeURIComponent(category)}/app/${encodeURIComponent(app)}/policy`,
    integrationRouteRevalidate: (routeId: string) => `/internal/integration-routes/routes/${encodeURIComponent(routeId)}/revalidate`,
    integrationRouteRemove: (routeId: string) => `/internal/integration-routes/routes/${encodeURIComponent(routeId)}/remove`,
    integrationRouteSettings: (routeId: string) => `/internal/integration-routes/routes/${encodeURIComponent(routeId)}/settings`,
  },
  /** PLAT-2026-141 GĐ2 — phòng thử (L1): fixture + lượt thử, quyền `hq.lab.*`. */
  lab: {
    fixtures: "/internal/lab/fixtures",
    fixture: (id: string) => `/internal/lab/fixtures/${encodeURIComponent(id)}`,
    runs: "/internal/lab/runs",
  },
  partners: {
    list: () => "/internal/partners",
    // HQ-2026-008 — bộ thông số, gán app, phòng thử Tingee.
    createSet: (code: string) => `/internal/partners/${encodeURIComponent(code)}/sets`,
    set: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}`,
    testSet: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/test`,
    /** HQ-2026-013 · ADR-007 — số phiên bản + ân hạn của mọi bộ một sản phẩm; lịch sử / so sánh / khôi phục / cắt ân hạn. */
    setSummaries: (code: string) => `/internal/partners/${encodeURIComponent(code)}/set-summaries`,
    setHistory: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/history`,
    setHistoryCompare: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/history/compare`,
    setRestore: (setId: string, revision: number) =>
      `/internal/partners/sets/${encodeURIComponent(setId)}/history/${encodeURIComponent(String(revision))}/restore`,
    setGraceCut: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/grace/cut`,
    /** BANK-2026-038 T4.7 — cách đối tác tính phí Bube theo bộ. */
    pricing: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/pricing`,
    pricingEntry: (setId: string, entryId: string) =>
      `/internal/partners/sets/${encodeURIComponent(setId)}/pricing/${encodeURIComponent(entryId)}`,
    binding: (code: string, app: string, env: string) =>
      `/internal/partners/${encodeURIComponent(code)}/bindings/${encodeURIComponent(app)}/${encodeURIComponent(env)}`,
    labRun: (setId: string, action: string) =>
      `/internal/partners/sets/${encodeURIComponent(setId)}/lab/tingee/${encodeURIComponent(action)}`,
    /** Tin đã nhận của phòng thử — CHUNG mọi đối tác có phòng thử (Tingee, SePay Bank Hub). */
    labEvents: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/events`,
    /** HQ-2026-011 — URL hai kênh nhận tin, đích webhook, chốt chặn của phòng thử Bank Hub (không gọi SePay). */
    labBankHubInfo: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/sepay-bankhub/info`,
    /** HQ-2026-011 — gọi MỘT API Bank Hub (`BANKHUB_LAB_ACTIONS`). */
    labBankHubRun: (setId: string, action: string) =>
      `/internal/partners/sets/${encodeURIComponent(setId)}/lab/sepay-bankhub/${encodeURIComponent(action)}`,
    /** HQ-2026-012 — phòng thử HĐĐT: mô tả thao tác (không gọi nhà cung cấp) / chạy một thao tác qua bube-einvoice. */
    labEinvoiceDescribe: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/einvoice/describe`,
    invoiceWorkbench: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/einvoice/workbench`,
    labEinvoicePreview: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/einvoice/workbench/preview`,
    labEinvoiceRun: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/einvoice/run`,
    /** HQ-2026-014 · ADR-009 — tài khoản thử đã lưu của bộ sandbox (GET đọc · PUT lưu · DELETE quên). */
    /** EINV-2026-020 GĐ7 — quyền bán theo dịch vụ + kênh đại lý HĐĐT (Agency Core ở bube-einvoice). */
    /** PLAT-2026-171 — « Thử tra » nguồn tra MST của nền tảng (qua bube-einvoice). */
    partyLookupSourceTest: () => `/internal/partners/party-lookup-source/test`,
    entitlements: (code:string) => `/internal/partners/${encodeURIComponent(code)}/entitlements`,
    entitlement: (code:string,kind:string) => `/internal/partners/${encodeURIComponent(code)}/entitlements/${encodeURIComponent(kind)}`,
    commercial: (code: string) => `/internal/partners/${encodeURIComponent(code)}/commercial`,
    agencyHistory: (code: string, env: string) => `/internal/partners/${encodeURIComponent(code)}/agency/${encodeURIComponent(env)}/history`,
    agencyOperations: (code: string, env: string) => `/internal/partners/${encodeURIComponent(code)}/agency/${encodeURIComponent(env)}/operations`,
    agencyRetry: (code: string, env: string, operationId: string) =>
      `/internal/partners/${encodeURIComponent(code)}/agency/${encodeURIComponent(env)}/operations/${encodeURIComponent(operationId)}/retry`,
    /** PLAT-2026-112 — đích « Đăng ký webhook » + gợi ý Client ID bên nhận. */
    labWebhookTargets: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/tingee-webhook-targets`,
    /** PLAT-2026-112 — merchant / shop / VA của khoá (chỉ đọc ở Tingee) cho form « Đăng ký webhook ». */
    labWebhookScopes: (setId: string) => `/internal/partners/sets/${encodeURIComponent(setId)}/lab/tingee-webhook-scopes`,
    /** PLAT-2026-110 — mọi hàm SDK Tingee + cờ chỉ đọc (máy chủ đọc từ SDK đang cài). */
    labMethods: () => "/internal/partners/lab/tingee-methods",
    /** PLAT-2026-110 — quy tắc create-va (ngân hàng × loại TK × cách nhận). */
    labCreateVaRule: (bank: string, kind: string, mode: string) =>
      `/internal/partners/lab/tingee-create-va-rule?bank=${encodeURIComponent(bank)}&kind=${encodeURIComponent(kind)}&mode=${encodeURIComponent(mode)}`,
  },
  /** PLAT-2026-111 · ADR-032 — AI theo app. */
  ai: {
    list: () => "/internal/ai",
    feature: (app: string, feature: string) => `/internal/ai/${encodeURIComponent(app)}/features/${encodeURIComponent(feature)}`,
    limits: (app: string) => `/internal/ai/${encodeURIComponent(app)}/limits`,
  },
  /** PLAT-2026-111 — trạng thái đẩy cấu hình hub → app + « Đẩy lại ». */
  configSync: {
    list: () => "/internal/config-sync",
    push: (app: string) => `/internal/config-sync/${encodeURIComponent(app)}/push`,
  },
  attention: {
    list: () => "/internal/attention",
    acknowledge: (id: string) => `/internal/attention/${encodeURIComponent(id)}/acknowledge`,
  },
  audience: {
    overview: () => "/internal/audience/overview",
    series: () => "/internal/audience/series",
  },
  revenue: {
    overview: () => "/internal/revenue/overview",
    subscriptions: () => "/internal/revenue/subscriptions",
    plans: () => "/internal/revenue/plans",
    unmappedPlans: () => "/internal/revenue/plans/unmapped",
    mapPlan: (sourceSlug: string, nativePlanId: string) =>
      `/internal/revenue/plans/${encodeURIComponent(sourceSlug)}/${encodeURIComponent(nativePlanId)}`,
    /**
     * HQ-2026-002 — mã thu tiền đi trong THÂN, không trong đường dẫn.
     *
     * Vì vậy hai đường này không nhận tham số: chúng là `POST`, kể cả `lookupTransfer` vốn chỉ đọc.
     */
    lookupTransfer: () => "/internal/revenue/transfers/lookup",
    confirmTransfer: () => "/internal/revenue/transfers/confirm",
  },
  /** HQ-2026-003 — chế độ khai của máy thu chung bube-banktransfer. Hub không lưu: đi thẳng sang bên đó. */
  banktransfer: {
    /** HQ-2026-007 — online / offline của bube-banktransfer (máy chủ + máy hub). */
    status: () => "/internal/banktransfer/status",
    sharedHubSettings: () => "/internal/banktransfer/shared-hub/settings",
    /** APK nội bộ « Bube Bank Transfer Hub » — chỉ dev (`hq.banktransfer.hubapk`). */
    hubApk: () => "/internal/banktransfer/shared-hub/apk",
    /** Máy hub (BANK-2026-028) — mã ghép + duyệt là việc của nhân viên trong hub. */
    hubDevices: () => "/internal/banktransfer/shared-hub/devices",
    hubInstallCodes: () => "/internal/banktransfer/shared-hub/install-codes",
    hubDeviceDecision: (deviceId: string) => `/internal/banktransfer/shared-hub/devices/${encodeURIComponent(deviceId)}/decision`,
    /** PLAT-2026-112 — kết nối Tingee của mọi công ty + gửi lại đăng ký webhook theo VA (chỉ dev, không có trên dash khách). */
    tingeeReadCertification: () => "/internal/partner-releases/tingee-readonly-certification",
    tingeeConnections: () => "/internal/banktransfer/tingee-connections",
    tingeeResubscribe: (connectionId: string) =>
      `/internal/banktransfer/tingee-connections/${encodeURIComponent(connectionId)}/webhook/subscribe`,
    /** BANK-2026-038 — đối tác ngân hàng: cửa vào theo đối tác, đình chỉ / lệch tính phí, đối chiếu VA. */
    partnerHooks: () => "/internal/banktransfer/partner-hooks",
    partnerBilling: () => "/internal/banktransfer/partner-billing",
    suspension: (connectionId: string) => `/internal/banktransfer/provider-connections/${encodeURIComponent(connectionId)}/suspension`,
    suspensionLift: (connectionId: string) => `/internal/banktransfer/provider-connections/${encodeURIComponent(connectionId)}/suspension/lift`,
    tingeeVaAudit: () => "/internal/banktransfer/tingee-va-audit",
    /** PLAT-2026-134 — link App Link phía bt: xem, đổi intent CỦA bt, gửi lại lượt quá hạn. */
    appLinks: () => "/internal/banktransfer/app-links",
    appLinkIntent: (linkId: string) => `/internal/banktransfer/app-links/${encodeURIComponent(linkId)}/intent`,
    appLinkRedrive: (linkId: string) => `/internal/banktransfer/app-links/${encodeURIComponent(linkId)}/redrive`,
    /** BANK-2026-039 — báo cáo đối tác (số đối tác + số Bube, chi phí, ngân sách). */
    partnerReport: () => "/internal/banktransfer/partner-report",
    partnerReportMerchant: (workspaceId: string) => `/internal/banktransfer/partner-report/merchants/${encodeURIComponent(workspaceId)}`,
    partnerReportRefresh: () => "/internal/banktransfer/partner-report/refresh",
    partnerBudgets: () => "/internal/banktransfer/partner-budgets",
    partnerBudget: (id: string) => `/internal/banktransfer/partner-budgets/${encodeURIComponent(id)}`,
    /** PLAT-2026-122 — ví trả trước (Tingee) + webhook bị chặn + hạn mức, kèm sổ ví. */
    partnerWallets: () => "/internal/banktransfer/partner-wallets",
    partnerWalletEntries: () => "/internal/banktransfer/partner-wallets/entries",
    partnerWalletEntry: (id: string) => `/internal/banktransfer/partner-wallets/entries/${encodeURIComponent(id)}`,
  },
  /** PLAT-2026-114 — quản trị khách theo app. Hub không lưu: đi thẳng sang app. */
  customers: {
    apps: () => "/internal/customers/apps",
    workspaces: (app: string) => `/internal/customers/${encodeURIComponent(app)}/workspaces`,
    workspace: (app: string, id: string) =>
      `/internal/customers/${encodeURIComponent(app)}/workspaces/${encodeURIComponent(id)}`,
    plan: (app: string, id: string) =>
      `/internal/customers/${encodeURIComponent(app)}/workspaces/${encodeURIComponent(id)}/plan`,
    users: (app: string) => `/internal/customers/${encodeURIComponent(app)}/users`,
  },
  /** PLAT-2026-173 — tình trạng máy chủ lấy hóa đơn VN: hub hỏi bube-einvoice khi mở trang, không lưu gì. */
  acquisitionOps: () => "/internal/acquisition-ops",
  /** PLAT-2026-174 — bán lại dịch vụ đối tác: hub sở hữu, lưu ở schema `hq`. */
  resale: {
    options: () => "/internal/resale/options",
    offerings: () => "/internal/resale/offerings",
    plans: () => "/internal/resale/plans",
    requests: () => "/internal/resale/requests",
    transition: (id: string) => `/internal/resale/requests/${encodeURIComponent(id)}/transition`,
    expiry: () => "/internal/resale/expiry",
    subscriptions: () => "/internal/resale/subscriptions",
  },
  /** PLAT-2026-156 — sổ lượt hoá đơn Bube: hub chuyển lệnh sang bube-einvoice, không lưu gì. */
  invoiceQuota: {
    workspaces: () => "/internal/invoice-quota/workspaces",
    scopes: (workspaceId: string) => `/internal/invoice-quota/workspaces/${encodeURIComponent(workspaceId)}/scopes`,
    ledger: () => "/internal/invoice-quota/ledger",
    command: (commandId: string) => `/internal/invoice-quota/commands/${encodeURIComponent(commandId)}`,
    grants: () => "/internal/invoice-quota/grants",
    fulfillments: () => "/internal/invoice-quota/fulfillments",
    cutover: () => "/internal/invoice-quota/cutover",
    reversals: () => "/internal/invoice-quota/reversals",
    policy: () => "/internal/invoice-quota/policy",
  },
  /** PLAT-2026-107 — thẻ tích hợp của bube-shop. Hub không lưu: đi thẳng sang bube-shop. */
  shopIntegrationCards: {
    list: () => "/internal/shop-integration-cards",
  },
  catalog: {
    products: () => "/internal/catalog/products",
    product: (slug: string) => `/internal/catalog/products/${encodeURIComponent(slug)}`,
    teams: () => "/internal/catalog/teams",
    team: (slug: string) => `/internal/catalog/teams/${encodeURIComponent(slug)}`,
    teamMembers: (slug: string) => `/internal/catalog/teams/${encodeURIComponent(slug)}/members`,
    teamMember: (slug: string, userId: string) =>
      `/internal/catalog/teams/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`,
    ownerships: () => "/internal/catalog/ownerships",
    dependencies: () => "/internal/catalog/dependencies",
    service: (slug: string) => `/internal/catalog/services/${encodeURIComponent(slug)}`,
  },
  staff: {
    list: () => "/internal/staff",
    role: (userId: string) => `/internal/staff/${encodeURIComponent(userId)}/role`,
    disable: (userId: string) => `/internal/staff/${encodeURIComponent(userId)}/disable`,
  },
  /** PLAT-2026-115 — bàn xử lý ticket hỗ trợ. */
  support: {
    inbox: () => "/internal/support/tickets",
    counts: () => "/internal/support/counts",
    agents: () => "/internal/support/agents",
    ticket: (app: string, id: string) => `/internal/support/tickets/${encodeURIComponent(app)}/${encodeURIComponent(id)}`,
    action: (app: string, id: string, action: "reply" | "status" | "priority" | "notes" | "assign") =>
      `/internal/support/tickets/${encodeURIComponent(app)}/${encodeURIComponent(id)}/${action}`,
  },
  feedback: {
    list: () => "/internal/feedback",
    summary: () => "/internal/feedback/summary",
    detail: (sourceSlug: string, sourceRef: string) =>
      `/internal/feedback/${encodeURIComponent(sourceSlug)}/${encodeURIComponent(sourceRef)}`,
    notes: (sourceSlug: string, sourceRef: string) =>
      `/internal/feedback/${encodeURIComponent(sourceSlug)}/${encodeURIComponent(sourceRef)}/notes`,
  },
  opsInternal: {
    collections: () => "/internal/ops/collections",
    run: (id: string) => `/internal/ops/runs/${encodeURIComponent(id)}`,
    drift: () => "/internal/ops/drift",
    runs: () => "/internal/ops/runs",
    cursors: () => "/internal/ops/cursors",
    audit: () => "/internal/ops/audit",
  },
  ops: {
    health: () => "/ops/health",
  },
} as const;
